import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { http, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Clock, Download, Copy as CopyIcon, ShoppingBag, Wallet } from "lucide-react";
import { toast } from "sonner";

/**
 * Two roles for this page, decided by URL:
 *   /gift-cards/thank-you?code=XXX  (Purchaser's confirmation after checkout/manual)
 *   /gift-card/:code                 (Recipient's view — what they get when they open the email)
 */
export default function GiftCardView({ purchaserMode }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();
  const code = (params.code || search.get("code") || "").toUpperCase();
  const sessionId = search.get("session_id");
  const method = search.get("method");
  const amountQp = search.get("amount");
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  const [loadingAcct, setLoadingAcct] = useState(false);

  // eslint-disable-next-line
  useEffect(() => {
    if (!code) { setError("No code provided"); return; }
    let cancelled = false;
    const fetchCard = () => http.get(`/gift-cards/balance/${code}`)
      .then(({ data }) => { if (!cancelled) setCard(data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || "Code not recognised"); });
    if (sessionId) {
      http.get(`/gift-cards/confirm-payment?session_id=${encodeURIComponent(sessionId)}`)
        .finally(fetchCard);
    } else {
      fetchCard();
    }
    return () => { cancelled = true; };
  }, [code, sessionId]);

  const copy = (txt, label = "Copied") =>
    navigator.clipboard?.writeText(txt).then(() => toast.success(label)).catch(() => {});

  const loadToAccount = async () => {
    if (!user) { toast.error("Sign in first to load to your account"); nav("/login"); return; }
    setLoadingAcct(true);
    try {
      const { data } = await http.post("/gift-cards/load-to-account", { code });
      toast.success(`$${data.loaded.toFixed(2)} loaded — your balance is now $${data.new_account_balance.toFixed(2)}.`);
      setCard(c => ({ ...c, balance: 0, status: "redeemed" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load card");
    } finally { setLoadingAcct(false); }
  };

  if (error) {
    return <Layout><div className="py-24 text-center font-ui text-[#9a9690]" data-testid="gc-error">{error}</div></Layout>;
  }
  if (!card) {
    return <Layout><div className="py-24 text-center font-ui text-[#9a9690]">Loading your gift card…</div></Layout>;
  }

  const isPending = card.status === "pending_payment";
  const isActive = card.status === "active";
  const isRedeemed = card.status === "redeemed";

  // Purchaser confirmation for manual payments
  const manualMethod = method && method !== "stripe";

  return (
    <Layout>
      <section className="bg-black pt-12 pb-24">
        <div className="max-w-[920px] mx-auto px-4 md:px-6">
          {/* Status banner */}
          <div className="text-center mb-8">
            {purchaserMode && manualMethod && isPending && (
              <>
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-2">Almost there</div>
                <h1 className="font-display-saira text-4xl md:text-5xl text-[#F5F2EB]">Send Payment to Activate</h1>
                <p className="font-body text-[#9a9690] mt-3 max-w-xl mx-auto">Send <span className="text-[#C9A055] font-semibold">${(amountQp || card.amount).toString().replace(".00","")}</span> to <button onClick={() => copy(method === "venmo" ? "@MickeyTrivett" : "$MickeyTrivett", "Handle copied")} className="text-[#D4B07B] underline-offset-2 hover:underline">{method === "venmo" ? "@MickeyTrivett on Venmo" : "$MickeyTrivett on Cash App"}</button> with the code <button onClick={() => copy(card.code, "Code copied")} className="font-mono text-[#F5F2EB] underline-offset-2 hover:underline">{card.code}</button> in the note. Mickey activates within 24 hours.</p>
              </>
            )}
            {purchaserMode && !manualMethod && isActive && (
              <>
                <div className="inline-flex items-center gap-2 text-emerald-400 font-ui text-[11px] tracking-[0.32em] uppercase mb-3">
                  <CheckCircle2 size={16}/> Payment Received
                </div>
                <h1 className="font-display-saira text-4xl md:text-5xl text-[#F5F2EB]">Your gift card is on its way</h1>
                <p className="font-body text-[#9a9690] mt-3">We just emailed {card.recipient_name || "the recipient"} their card. You&apos;ll get a copy at your email too.</p>
              </>
            )}
            {!purchaserMode && (
              <>
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-2">A truth, just for you</div>
                <h1 className="font-display-saira text-4xl md:text-5xl text-[#F5F2EB]">{card.recipient_name ? `${card.recipient_name}, this is yours.` : "This is yours."}</h1>
                {card.personal_message && (
                  <p className="font-script text-2xl text-[#C9A055] mt-4 max-w-xl mx-auto italic">&ldquo;{card.personal_message}&rdquo;</p>
                )}
              </>
            )}
          </div>

          {/* Card image */}
          <div className="relative max-w-[820px] mx-auto" data-testid="gift-card-img-wrap">
            <img src={`${API_BASE}/api/gift-cards/image/${code}.png`} alt="Gift card" className="w-full h-auto border border-[#C9A055]/40 shadow-[0_30px_80px_rgba(0,0,0,0.6)]" data-testid="gift-card-img"/>
          </div>

          {/* Actions */}
          <div className="max-w-[820px] mx-auto mt-8 grid sm:grid-cols-2 gap-3">
            <a
              href={`${API_BASE}/api/gift-cards/image/${code}.png`}
              download={`stc-gift-card-${code}.png`}
              className="btn-outline w-full"
              data-testid="gc-download">
              <Download size={16}/> Download Card
            </a>
            <button
              onClick={() => copy(code, "Code copied")}
              className="btn-outline w-full"
              data-testid="gc-copy-code">
              <CopyIcon size={16}/> Copy Code · {code}
            </button>
          </div>

          {/* Recipient CTAs */}
          {!purchaserMode && isActive && (
            <div className="max-w-[820px] mx-auto mt-10 border border-[#C9A055]/30 bg-[#0a0a0a] p-6">
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4 text-center">Redeem it your way</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to={`/shop?gift=${code}`} className="btn-gold w-full" data-testid="gc-shop-with-code">
                  <ShoppingBag size={16}/> Shop with this code
                </Link>
                <button onClick={loadToAccount} disabled={loadingAcct} className="btn-outline w-full" data-testid="gc-load-account">
                  <Wallet size={16}/> {loadingAcct ? "Loading…" : "Load to my STC account"}
                </button>
              </div>
              <p className="font-body text-[12px] text-[#737373] text-center mt-4">
                Pick &ldquo;Shop with this code&rdquo; to apply ${card.balance.toFixed(2)} at checkout. Pick &ldquo;Load to my account&rdquo; to save the full balance for future purchases.
              </p>
            </div>
          )}

          {isRedeemed && (
            <div className="max-w-[820px] mx-auto mt-10 border border-emerald-700/30 bg-emerald-900/10 p-5 text-center" data-testid="gc-redeemed-banner">
              <CheckCircle2 size={20} className="text-emerald-400 mx-auto mb-2"/>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-emerald-400">Fully redeemed</div>
              <p className="font-body text-[#9a9690] mt-1">This card has been used. Thank you for carrying a truth.</p>
            </div>
          )}

          {isPending && !purchaserMode && (
            <div className="max-w-[820px] mx-auto mt-10 border border-[#C9A055]/30 bg-[#C9A055]/5 p-5 text-center" data-testid="gc-pending-banner">
              <Clock size={20} className="text-[#C9A055] mx-auto mb-2"/>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">Awaiting payment confirmation</div>
              <p className="font-body text-[#9a9690] mt-1">Mickey is finalising payment. This card will activate within 24 hours.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
